import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaExternalLinkAlt,
  FaFileAlt,
  FaTimes,
  FaUserPlus,
} from "react-icons/fa";
import Swal from "sweetalert2";
import Title from "../GenericComponents/Title";
import Paginations from "../GenericComponents/Pagination";
import RegistryStatCards from "../GenericComponents/RegistryStatCards";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import { fetchActiveEmployees } from "./registryApi";
import {
  assignUserPolicies,
  fetchAllActivePolicies,
  getAlreadyAssignedUserIds,
} from "./sopPolicyApi";
import {
  CMS_PRIMARY,
  SOP_PAGE_SHELL,
  SOP_TITLE_SHELL,
  SOP_POLICY_STAT_CARDS,
  MODULE_PAGE_SHELL_STYLE,
  formatPolicyDate,
  groupEmployeesByBranch,
} from "./sopPolicyConstants";

const BUTTON_BORDER_RESET = {
  border: "none",
  outline: "none",
  WebkitAppearance: "none",
  appearance: "none",
};

function PolicyCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200/90 bg-white p-3 shadow-sm">
      <div className="skeleton mb-2 h-3.5 w-2/3 rounded" />
      <div className="skeleton mb-2 h-4 w-20 rounded-full" />
      <div className="skeleton mb-1 h-2.5 w-full rounded" />
      <div className="skeleton mb-1 h-2.5 w-5/6 rounded" />
      <div className="skeleton mb-3 h-2.5 w-3/4 rounded" />
      <div className="skeleton h-7 w-full rounded-md" />
      <div className="flex gap-2">
        <div className="skeleton h-7 flex-1 rounded-md" />
        <div className="skeleton h-7 flex-1 rounded-md" />
      </div>
    </div>
  );
}

function PolicyCard({ policy, onAssign }) {
  const version = policy.current_version;
  const assignedCount = policy.assigned_users_count ?? policy.assigned_user_ids?.length ?? 0;

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-gray-200/90 bg-white p-3 shadow-sm transition hover:border-[#007BFF]/30 hover:shadow-md">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3
            className="truncate text-[13px] font-semibold leading-tight text-[#1a1f36]"
            title={policy.name}
          >
            {policy.name || "Untitled Policy"}
          </h3>
          <span className="mt-1.5 inline-flex max-w-full items-center rounded-md bg-slate-100 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-600">
            <span className="truncate">{policy.category?.name || "Uncategorized"}</span>
          </span>
        </div>
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-500">
          <FaFileAlt size={12} />
        </div>
      </div>

      <p
        className="mb-3 line-clamp-3 min-h-[3.6rem] text-[11px] leading-[1.2rem] text-gray-500"
        title={policy.description || undefined}
      >
        {policy.description?.trim() ? policy.description.trim() : "No description provided."}
      </p>

      {version ? (
        <div className="mb-3 rounded-md border border-gray-100 bg-slate-50/80 px-2.5 py-2">
          <div className="flex items-center justify-between gap-2 text-[10px]">
            <div className="min-w-0">
              <span className="text-gray-400">Commence</span>
              <p className="truncate font-medium text-[#35314d]">
                {formatPolicyDate(version.commencement_date)}
              </p>
            </div>
            <div className="h-6 w-px shrink-0 bg-gray-200" />
            <div className="min-w-0 text-right">
              <span className="text-gray-400">Review</span>
              <p className="truncate font-medium text-[#35314d]">
                {formatPolicyDate(version.review_date)}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-3 rounded-md border border-dashed border-gray-200 px-2.5 py-2 text-[10px] text-gray-400">
          No version available
        </div>
      )}

      <div className="mt-auto space-y-2">
        <div className="flex gap-2">
          {version?.file_url ? (
            <a
              href={version.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-[#007BFF]/20 bg-[#007BFF]/5 px-2 py-1.5 text-[11px] font-semibold text-[#007BFF] transition hover:bg-[#007BFF] hover:text-white"
            >
              View Document
              <FaExternalLinkAlt size={9} />
            </a>
          ) : (
            <span className="flex flex-1 items-center justify-center text-[10px] text-gray-400">
              Unavailable
            </span>
          )}
          <button
            type="button"
            onClick={() => onAssign(policy)}
            className="inline-flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-semibold text-white transition hover:opacity-90"
            style={{ ...BUTTON_BORDER_RESET, backgroundColor: CMS_PRIMARY }}
          >
            <FaUserPlus size={10} />
            Assign
          </button>
        </div>
        
      </div>
    </article>
  );
}

function AssignEmployeesModal({
  open,
  loading,
  branchGroups,
  pendingIds,
  onToggle,
  onToggleBranch,
  onAssign,
  onClose,
  assigning = false,
  policyName = "",
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close modal backdrop"
        onClick={onClose}
      />
      <div
        className="relative flex max-h-[85vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-employees-title"
      >
        <div className="flex shrink-0 items-start justify-between border-b border-gray-100 px-5 py-3.5">
          <div>
            <p id="assign-employees-title" className="text-sm font-semibold text-[#35314d]">
              Assign employees
              {policyName ? (
                <span className="font-normal text-gray-500"> · {policyName}</span>
              ) : null}
            </p>
            <p className="mt-0.5 text-[11px] text-gray-500">
              Already assigned users are pre-selected. Add or remove selections, then assign.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            style={BUTTON_BORDER_RESET}
            aria-label="Close"
          >
            <FaTimes size={14} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {[...Array(18)].map((_, index) => (
                <div key={`assign-skel-${index}`} className="skeleton h-8 rounded-md" />
              ))}
            </div>
          ) : branchGroups.length === 0 ? (
            <p className="py-16 text-center text-xs text-gray-500">
              No employees available to assign.
            </p>
          ) : (
            <div className="space-y-3">
              {branchGroups.map((group, groupIndex) => {
                const groupUserIds = group.users.map((user) => String(user.id));
                const selectedInGroup = groupUserIds.filter((id) => pendingIds.has(id)).length;
                const allGroupSelected =
                  groupUserIds.length > 0 && selectedInGroup === groupUserIds.length;
                const someGroupSelected =
                  selectedInGroup > 0 && selectedInGroup < groupUserIds.length;
                const isAltBranch = groupIndex % 2 === 1;

                return (
                <div
                  key={group.branch_id ?? group.branch_name}
                  className={`rounded-lg border p-3 ${
                    isAltBranch
                      ? "border-[#007BFF]/15 bg-[#007BFF]/5"
                      : "border-gray-200 bg-slate-50/70"
                  }`}
                >
                  <label
                    className={`mb-2.5 flex cursor-pointer items-center gap-2 border-b pb-2 ${
                      isAltBranch ? "border-[#007BFF]/10" : "border-gray-200/80"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={allGroupSelected}
                      ref={(node) => {
                        if (node) node.indeterminate = someGroupSelected;
                      }}
                      onChange={() => onToggleBranch(group.users, !allGroupSelected)}
                      className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]"
                      aria-label={`Select all in ${group.branch_name}`}
                    />
                    <span
                      className={`text-[11px] font-semibold uppercase tracking-wide ${
                        isAltBranch ? "text-[#007BFF]" : "text-gray-600"
                      }`}
                    >
                      {group.branch_name}
                    </span>
                  </label>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                    {group.users.map((user) => {
                      const checked = pendingIds.has(String(user.id));
                      return (
                        <label
                          key={user.id}
                          className={`flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 transition ${
                            checked
                              ? "border-[#007BFF]/30 bg-white shadow-sm"
                              : "border-gray-200/90 bg-white hover:border-gray-300 hover:bg-white"
                          }`}
                          title={user.name}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => onToggle(String(user.id))}
                            className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]"
                          />
                          <span className="truncate text-[11px] font-medium text-[#35314d]">
                            {user.name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/80 px-5 py-3">
          <span className="text-[11px] text-gray-500">{pendingIds.size} selected</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
              style={BUTTON_BORDER_RESET}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={pendingIds.size === 0 || loading || assigning}
              onClick={onAssign}
              className="rounded-md px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ ...BUTTON_BORDER_RESET, backgroundColor: CMS_PRIMARY }}
            >
              {assigning ? "Assigning..." : "Assign"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SOP_Policies() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  });

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [groupedEmployees, setGroupedEmployees] = useState([]);
  const [assignPolicyCatalog, setAssignPolicyCatalog] = useState([]);
  const [pendingAssignIds, setPendingAssignIds] = useState(new Set());
  const [assignPolicyId, setAssignPolicyId] = useState("");
  const [assignPolicyName, setAssignPolicyName] = useState("");
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    const pers = getPermissions();
    if (pers) setPermissions(pers);
  }, []);

  const fetchSOPPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.currentPage,
        perPage: pagination.perPage,
      };

      const response = await apiUrl.get("/fetch-active-policies", { params });

      setPolicies(response?.data?.data || []);

      const pageInfo = response?.data?.pagination;
      if (pageInfo) {
        setPagination((prev) => ({
          ...prev,
          total: pageInfo.total || 0,
          lastPage: pageInfo.lastPage || 1,
          currentPage: pageInfo.currentPage || prev.currentPage,
          perPage: pageInfo.perPage || prev.perPage,
        }));
      }
    } catch (error) {
      console.error("Error fetching SOP policies:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to fetch policies",
        icon: "error",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.perPage]);

  useEffect(() => {
    fetchSOPPolicies();
  }, [fetchSOPPolicies]);

  const loadAssignModalData = useCallback(async (policyId) => {
    if (!policyId) return;

    setLoadingEmployees(true);
    try {
      const policyIds = new Set([String(policyId)]);
      const [employeeResult, policyCatalog] = await Promise.all([
        fetchActiveEmployees(),
        fetchAllActivePolicies(),
      ]);
      setGroupedEmployees(employeeResult.grouped || []);
      setAssignPolicyCatalog(policyCatalog);
      setPendingAssignIds(getAlreadyAssignedUserIds(policyCatalog, policyIds));
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || error.message || "Failed to load assign data",
        icon: "error",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      setGroupedEmployees([]);
      setAssignPolicyCatalog([]);
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  useEffect(() => {
    if (assignModalOpen && assignPolicyId) {
      loadAssignModalData(assignPolicyId);
    }
  }, [assignModalOpen, assignPolicyId, loadAssignModalData]);

  const branchGroups = useMemo(
    () => groupEmployeesByBranch(groupedEmployees),
    [groupedEmployees],
  );

  const openAssignModalForPolicy = (policy) => {
    setAssignPolicyId(String(policy.id));
    setAssignPolicyName(policy.name || "Policy");
    setAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setAssignPolicyId("");
    setAssignPolicyName("");
    setPendingAssignIds(new Set());
  };

  const togglePendingAssign = (userId) => {
    setPendingAssignIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleBranchMembers = (users = [], selectAll) => {
    setPendingAssignIds((prev) => {
      const next = new Set(prev);
      users.forEach((user) => {
        const id = String(user.id);
        if (selectAll) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const handlePaginationCallback = (page, perPage) => {
    if (perPage && perPage !== "") {
      setPagination((prev) => ({
        ...prev,
        perPage: parseInt(perPage, 10),
        currentPage: 1,
      }));
    } else {
      setPagination((prev) => ({
        ...prev,
        currentPage: page,
      }));
    }
  };

  const handleAssign = async () => {
    if (!pendingAssignIds.size || !assignPolicyId) return;

    setAssigning(true);

    try {
      const userIds = [...pendingAssignIds].map(Number);
      const policyIds = [Number(assignPolicyId)];

      await assignUserPolicies(userIds, policyIds);

      closeAssignModal();
      await fetchSOPPolicies();

      Swal.fire({
        title: "Assigned",
        text: `${userIds.length} employee(s) assigned to ${assignPolicyName}.`,
        icon: "success",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        title: "Assign failed",
        text: error?.response?.data?.message || error.message || "Could not assign policies.",
        icon: "error",
      });
    } finally {
      setAssigning(false);
    }
  };

  const policyStats = useMemo(() => {
    const categories = new Set(policies.map((policy) => policy.category?.name).filter(Boolean));

    return {
      total: pagination.total,
      categories: categories.size,
    };
  }, [policies, pagination.total]);

  const perItem = Math.min(
    pagination.perPage,
    Math.max(0, pagination.total - (pagination.currentPage - 1) * pagination.perPage),
  );

  return (
    <>
      {permissions?.view_sop_and_policies ? (
        <>
          <div className={SOP_TITLE_SHELL}>
            <Title title="SOP & Policies" back={false} flushLeft />
          </div>

          <div className="tailwind-container">
            <div className={SOP_PAGE_SHELL} style={MODULE_PAGE_SHELL_STYLE}>
              <RegistryStatCards
                cards={SOP_POLICY_STAT_CARDS}
                stats={policyStats}
                loading={loading}
                className="mb-3"
              />

              {loading ? (
                <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {[...Array(8)].map((_, index) => (
                    <PolicyCardSkeleton key={`policy-skel-${index}`} />
                  ))}
                </div>
              ) : policies.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-200 bg-white px-5 py-12 text-center">
                  <FaFileAlt size={20} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm font-semibold text-[#35314d]">No policies found</p>
                  <p className="mx-auto mt-1 max-w-sm text-xs text-gray-500">
                    There are no active policies available to display right now.
                  </p>
                </div>
              ) : (
                <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {policies.map((policy) => (
                    <PolicyCard
                      key={policy.id}
                      policy={policy}
                      onAssign={openAssignModalForPolicy}
                    />
                  ))}
                </div>
              )}

              {pagination.total > 0 ? (
                <div className="mt-3">
                  <Paginations
                    currentPage={pagination.currentPage}
                    itemsPerPage={pagination.perPage}
                    lastPage={pagination.lastPage}
                    onPageChange={handlePaginationCallback}
                    totalItems={pagination.total}
                    perItem={perItem}
                  />
                </div>
              ) : null}
            </div>
          </div>

          <AssignEmployeesModal
            open={assignModalOpen}
            loading={loadingEmployees}
            branchGroups={branchGroups}
            pendingIds={pendingAssignIds}
            onToggle={togglePendingAssign}
            onToggleBranch={toggleBranchMembers}
            onAssign={handleAssign}
            onClose={closeAssignModal}
            assigning={assigning}
            policyName={assignPolicyName}
          />
        </>
      ) : null}
    </>
  );
}

export default SOP_Policies;
